import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  inject,
  OnChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DepositConfirmationComponent } from '../deposit-confirmation/deposit-confirmation.component';
import { Subject } from 'rxjs';
import { DepositFormComponent, DepositFormData } from '../deposit-form/deposit-form.component';
import { RiskLevelEnum } from '@olv/ui-shared/enums/task.enums';
import { WalletDto, WalletTypeEnum } from '@olv/ui-shared/models/wallet.model';
import { PillSearchComponent, SearchState } from '@olv/ui-shared/ui-shared/pill-search/pill-search.component';
import { WalletCardComponent, walletCardHeight } from '@olv/ui-shared/ui-shared/wallet-card/wallet-card.component';
import { Resources, Resource } from '@olv/ui-shared/models/settlement-permissions.model';
import { AppContextService } from '@olv/ui-shared/services/app-context.service';
import { LoadingSpinnerComponent } from '@olv/ui-shared/ui-shared/loading-spinner/loading-spinner.component';
import { VirtualScrollComponent } from '@olv/ui-shared/ui-shared/virtual-scroll/virtual-scroll.component';
import { Merchant } from '@olv/ui-shared/models/merchant.model';

export interface DepositConfirmationData {
  walletId?: string;
  entityName: string;
  walletAddress: string;
  qrCodeData: string;
  networkType?: number;
  amount?: string;
  currency?: string;
  userType?: string;
  identifier?: string;
  walletType?: string;
  blockchainLabel?: string;
}

@Component({
  selector: 'app-deposit-container',
  standalone: true,
  imports: [
    CommonModule,
    DepositFormComponent,
    PillSearchComponent,
    WalletCardComponent,
    DepositConfirmationComponent,
    LoadingSpinnerComponent,
    VirtualScrollComponent,
  ],
  templateUrl: './deposit-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./deposit-container.component.scss'],
})
export class DepositContainerComponent implements OnInit, OnDestroy, OnChanges {
  protected readonly walletCardHeight = walletCardHeight;
  @ViewChild(DepositFormComponent) private depositForm?: DepositFormComponent;

  @Input() formTitle = 'Deposit';
  @Input() submitButtonText = 'Next';
  @Input() walletGenerationText = 'Generate Wallet';
  @Input() enableWalletGeneration = true;
  @Input() walletPanelTitle = 'Select Wallet';
  @Input() confirmationTitle = 'Deposit';
  @Input() entityLabel = 'Entity';
  @Input() entityType = 'entity'; // merchant, system, etc.
  @Input() actionButtonText = 'New Deposit';

  // Permissions context to forward to child components
  @Input() walletResource: Resource = Resources.MerchantWallets;
  @Input() transactionResource: Resource = Resources.MerchantTransactions;

  // Input properties from parent component
  @Input() wallets: WalletDto[] = [];
  @Input() merchant?: Merchant;
  @Input() isLoading = false;
  @Input() error: string | null = null;
  @Input() entityId = ''; // Generic entity ID

  // Output events
  @Output() depositCompleted = new EventEmitter<DepositConfirmationData>();
  @Output() walletCreated = new EventEmitter<string>();
  @Output() newDepositRequested = new EventEmitter<void>();

  private readonly appCtx = inject(AppContextService);

  // View state
  showConfirmation = false;
  confirmationData: DepositConfirmationData | null = null;

  // Search state
  searchText = '';

  // Selected blockchain for filtering
  selectedBlockchainType: number | null = null;

  // Derived state
  filteredWallets: WalletDto[] = [];

  // Selected wallet tracking
  selectedWalletId: string | null = null;
  selectedWallet: WalletDto | null = null;
  createdWalletIds = new Set<string>();

  selectedCurrency: string | undefined = undefined;

  isSettlementApp = false; // Flag to indicate if the current app is merchant or not

  // Cleanup subscription
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    // Set the initial blockchain filter to the default blockchain type (Tron)
    this.selectedBlockchainType = null;

    const appName = this.appCtx.getAppName();
    this.isSettlementApp = appName === 'settlement' ? true : false;
    // Initialize filtered wallets
    this.applyFilter();
  }

  // When wallets input changes, update filtered wallets
  ngOnChanges(): void {
    this.applyFilter();
  }

  /**
   * Handle search input changes
   */
  onSearchChange(searchState: SearchState): void {
    this.searchText = searchState.value;
    this.applyFilter();
  }

  /**
   * Apply search filter to wallets
   */
  private applyFilter(): void {
    // Start with all wallets except one-time wallet
    let filtered = this.wallets.filter(
      (w) => w.walletType !== WalletTypeEnum.OnetimeUse || this.createdWalletIds.has(w.id),
    );
    // Apply blockchain filter if one is selected
    if (this.selectedBlockchainType !== null) {
      filtered = filtered.filter((wallet) => wallet.networkType === this.selectedBlockchainType);
    }
    // Then apply text search filter if there is search text
    if (this.searchText) {
      const search = this.searchText.toLowerCase().trim();
      filtered = filtered.filter(
        (wallet) =>
          wallet.address.toLowerCase().includes(search) ||
          (wallet.walletName && wallet.walletName.toLowerCase().includes(search)),
      );
    }
    this.filteredWallets = filtered;
  }

  /**
   * Handle wallet selection
   * @param wallet The selected wallet or null for "New Wallet"
   */
  onWalletSelected(wallet: WalletDto | null): void {
    this.selectedWallet = wallet;
    this.selectedWalletId = wallet?.id ?? null;

    this.onBlockchainChanged(wallet?.networkType ?? null);
  }

  onWalletCardSelected(wallet: WalletDto): void {
    this.onWalletSelected(wallet);
    this.depositForm?.scrollWalletIntoView();
  }

  /**
   * Handles the form submission event.
   * Switches to the confirmation view.
   * @param formData The data from the deposit form.
   */
  onDepositFormSubmit(formData: DepositFormData): void {
    // Store the submitted data
    this.confirmationData = {
      walletId: formData.walletId,
      entityName: formData.entityName || 'Default Entity',
      walletAddress: formData.walletAddress || 'Default Address',
      qrCodeData: formData.qrCodeData || '',
      networkType: formData.networkType,
      amount: formData.amount,
      currency: formData.currency || this.selectedCurrency,
      userType: formData.userType,
      identifier: formData.identifier,
      walletType: formData.walletType,
      blockchainLabel: formData.blockchainLabel,
    };

    // If a wallet ID was provided, keep that wallet selected in the grid
    if (formData.walletId) {
      this.selectedWalletId = formData.walletId;
    }

    // Switch to confirmation view
    this.showConfirmation = true;

    // Emit the deposit completed event
    this.depositCompleted.emit(this.confirmationData);
  }

  /**
   * Handles the "New Deposit" event from the confirmation component.
   * Switches back to the form view.
   */
  handleNewDeposit(): void {
    this.showConfirmation = false;
    this.confirmationData = null;
    this.selectedBlockchainType = null;
    this.selectedWalletId = null;
    this.createdWalletIds.clear();
    this.selectedWallet = null;
    this.applyFilter();

    // Emit the new deposit requested event
    this.newDepositRequested.emit();
  }

  /**
   * Check if a wallet is currently selected
   * @param wallet The wallet to check
   * @returns True if the wallet is selected
   */
  isWalletSelected(walletId: string | null): boolean {
    return this.selectedWalletId === walletId;
  }
  /**
   * Check if a wallet is new
   * @param wallet The wallet to check
   * @returns True if the wallet is new
   */
  isWalletNew(walletId: string | null): boolean {
    return walletId !== null && this.createdWalletIds.has(walletId);
  }

  /**
   * Convert wallet balances to the format expected by wallet-card component
   * Shows appropriate currencies based on wallet network type
   * @param wallet The wallet to get currencies for
   * @returns Array of currency objects with name and amount
   */
  getWalletCurrencies(wallet: WalletDto): { name: string; amount: number; displayPrecision: number }[] {
    if (!wallet?.balance || !wallet.balance.currencyBalances || wallet.balance.currencyBalances.length === 0) {
      return [];
    }

    return wallet.balance.currencyBalances.map((balance) => ({
      name: balance.currencySymbol,
      amount: balance.totalAmountInCurrency,
      displayPrecision: balance.currencyDisplayPrecision,
    }));
  }

  getRiskLevelName(riskLevel?: RiskLevelEnum): string {
    const riskLevelMap: Record<RiskLevelEnum, string> = {
      [RiskLevelEnum.Unsupported]: 'unsupported',
      [RiskLevelEnum.NotAssessed]: 'notassessed',
      [RiskLevelEnum.Unknown]: 'unknown',
      [RiskLevelEnum.Low]: 'low',
      [RiskLevelEnum.Medium]: 'medium',
      [RiskLevelEnum.High]: 'high',
      [RiskLevelEnum.Severe]: 'severe',
    };

    return riskLevel !== undefined ? riskLevelMap[riskLevel] : 'Not Assessed';
  }

  /**
   * Gets the currently selected wallet object
   * @returns The selected wallet object or undefined if no wallet is selected
   */
  getSelectedWallet(): WalletDto | undefined {
    if (this.selectedWalletId === null) {
      return undefined;
    }

    return this.wallets.find((wallet) => wallet.id === this.selectedWalletId);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle blockchain selection change from the deposit form
   * @param blockchainType The selected blockchain network type
   */
  onBlockchainChanged(blockchainType: number | null): void {
    if (!blockchainType || (this.selectedBlockchainType && this.selectedBlockchainType !== blockchainType)) {
      this.selectedWalletId = null;
      this.selectedWallet = null;
    }
    this.selectedBlockchainType = blockchainType;

    this.applyFilter();
  }

  onWalletCreated(updatedWallet: WalletDto): void {
    if (!updatedWallet) return;

    this.selectedWalletId = updatedWallet.id;
    this.createdWalletIds.add(updatedWallet.id);

    this.walletCreated.emit(updatedWallet.id);

    this.wallets = [updatedWallet, ...this.wallets];
    this.applyFilter();
  }
  onCurrencySelected(currency: string | undefined) {
    this.selectedCurrency = currency;
  }
}
